import './target-frame.css';

import type { StatusId } from '@shefira/shared';

import { EffectStrip } from './effect-strip.js';
import { uiFormat, uiText } from './text.js';

/**
 * The creature the player is fighting or has picked (Space), at the top of
 * the screen: its name, level and health, and under them the states it
 * suffers (ADR 0048) with their time left, so a stun or a bleed can be read
 * without squinting at the marks over its head.
 *
 * A view of the synchronised state and of the `status` messages only. The
 * room announces every state to everyone nearby; the frame keeps them per
 * creature, so picking another target shows what already weighs on it.
 */
export interface TargetFrameState {
  mobId: string;
  name: string;
  level?: number;
  hp: number;
  maxHp: number;
  /** Fights only when struck (ADR 0058): « Passif » after the name, « Agressif » otherwise. */
  passive: boolean;
  /** The boss bar is up: the frame goes under it rather than over it. */
  belowBossBar: boolean;
  /** The boss of that bar: it already says the name and the health, only the states are left. */
  statesOnly: boolean;
}

interface Laid {
  endsAt: number;
  durationMs: number;
}

export class TargetFrame {
  private readonly root: HTMLElement;
  private readonly card: HTMLElement;
  private readonly name: HTMLElement;
  private readonly temper: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly value: HTMLElement;
  private readonly strip = new EffectStrip({ tipBelow: true });
  /** Every creature's states as announced, performance.now clock. */
  private readonly laid = new Map<string, Map<StatusId, Laid>>();
  private shown: TargetFrameState | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'target-frame';
    this.root.hidden = true;
    this.card = document.createElement('div');
    this.card.className = 'target-frame__card';
    const heading = document.createElement('div');
    heading.className = 'target-frame__heading';
    this.name = document.createElement('div');
    this.name.className = 'target-frame__name';
    this.temper = document.createElement('div');
    this.temper.className = 'target-frame__temper';
    heading.append(this.name, this.temper);
    const track = document.createElement('div');
    track.className = 'target-frame__track';
    this.fill = document.createElement('div');
    this.fill.className = 'target-frame__fill';
    this.value = document.createElement('div');
    this.value.className = 'target-frame__value';
    track.append(this.fill, this.value);
    this.card.append(heading, track);
    this.root.append(this.card, this.strip.root);
    document.body.append(this.root);
  }

  /** A state landed on a creature, target or not. */
  noteStatus(mobId: string, status: StatusId, durationMs: number): void {
    const now = performance.now();
    let states = this.laid.get(mobId);
    if (states === undefined) {
      states = new Map();
      this.laid.set(mobId, states);
    }
    const endsAt = now + durationMs;
    const current = states.get(status);
    if (current === undefined || endsAt > current.endsAt)
      states.set(status, { endsAt, durationMs });
    if (this.shown?.mobId === mobId) this.strip.show(status, durationMs);
    this.prune(now);
  }

  /** The creature to show, or null to hide the frame. Cheap to call every frame. */
  show(state: TargetFrameState | null): void {
    const now = performance.now();
    const was = this.shown;
    if (was?.mobId !== state?.mobId) {
      this.strip.clear();
      const states = state === null ? undefined : this.laid.get(state.mobId);
      for (const [status, laid] of states ?? []) {
        if (laid.endsAt > now) this.strip.showUntil(status, laid.endsAt, laid.durationMs);
      }
    }
    this.strip.tick(now);
    if (
      was?.mobId === state?.mobId &&
      was?.hp === state?.hp &&
      was?.maxHp === state?.maxHp &&
      was?.passive === state?.passive &&
      was?.belowBossBar === state?.belowBossBar &&
      was?.statesOnly === state?.statesOnly
    ) {
      return;
    }
    this.shown = state === null ? null : { ...state };
    this.root.hidden = state === null;
    if (state === null) return;
    this.root.classList.toggle('target-frame--under-boss', state.belowBossBar);
    this.root.classList.toggle('target-frame--states-only', state.statesOnly);
    this.name.textContent =
      state.level === undefined
        ? uiText(state.name)
        : uiFormat('{name} · Niv. {level}', { name: state.name, level: state.level });
    this.temper.textContent = uiText(state.passive ? 'Passif' : 'Agressif');
    this.temper.classList.toggle('target-frame__temper--passive', state.passive);
    const share = state.maxHp > 0 ? Math.max(0, Math.min(1, state.hp / state.maxHp)) : 0;
    this.fill.style.width = `${String(Math.round(share * 1000) / 10)}%`;
    this.value.textContent = `${String(state.hp)} / ${String(state.maxHp)}`;
  }

  /** Forget the states that wore off, so the map does not grow with every creature ever struck. */
  private prune(now: number): void {
    for (const [mobId, states] of this.laid) {
      for (const [status, laid] of states) if (laid.endsAt <= now) states.delete(status);
      if (states.size === 0) this.laid.delete(mobId);
    }
  }

  destroy(): void {
    this.root.remove();
  }
}
